import { Box, Typography } from "components";

export type PaymentMethod = "upi" | "netbanking";

interface Props {
  value: string;
  onChange: (value: PaymentMethod) => void;
  disabled?: boolean;
}

const OPTIONS: {
  value: PaymentMethod;
  label: string;
  subtitle: string;
}[] = [
  { value: "upi", label: "UPI", subtitle: "Pay instantly via any UPI app" },
  {
    value: "netbanking",
    label: "Net Banking",
    subtitle: "Transfer from your bank account",
  },
];

const RadioDot = ({ selected }: { selected: boolean }) => (
  <Box
    sx={{
      alignItems: "center",
      border: selected
        ? "5px solid var(--primary-900)"
        : "2px solid var(--neutral-300, #cbd5e1)",
      borderRadius: "50%",
      boxSizing: "border-box",
      display: "flex",
      height: 18,
      justifyContent: "center",
      width: 18,
    }}
  />
);

const PaymentMethodSelector = ({ value, onChange, disabled }: Props) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
    <Typography color="var(--black-900)" fontSize={14} fontWeight={600}>
      Payment Method
    </Typography>
    <Box
      sx={{
        display: "grid",
        gap: "16px",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
      }}
    >
      {OPTIONS.map((option) => {
        const selected = value === option.value;
        return (
          <Box
            key={option.value}
            role="radio"
            aria-checked={selected}
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && onChange(option.value)}
            onKeyDown={(e) => {
              if (!disabled && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onChange(option.value);
              }
            }}
            sx={{
              alignItems: "center",
              bgcolor: "var(--white)",
              border: selected
                ? "1px solid var(--primary-900)"
                : "1px solid var(--neutral-100)",
              borderRadius: "12px",
              boxShadow: selected ? "0 0 0 1px var(--primary-900)" : "none",
              cursor: disabled ? "not-allowed" : "pointer",
              display: "flex",
              gap: "12px",
              opacity: disabled ? 0.6 : 1,
              p: "16px",
              transition: "border-color 0.15s, box-shadow 0.15s",
            }}
          >
            <RadioDot selected={selected} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <Typography
                color="var(--black-900)"
                fontSize={14}
                fontWeight={600}
              >
                {option.label}
              </Typography>
              <Typography
                color="var(--neutral-500)"
                fontSize={12}
                fontWeight={500}
              >
                {option.subtitle}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  </Box>
);

export default PaymentMethodSelector;
